import {
  BookOpenIcon,
  InformationCircleIcon,
  LogoutIcon,
} from "@heroicons/react/solid";
import { IJitsiMeetExternalApi } from "@jitsi/react-sdk/lib/types";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiResponse } from "../../api/apiResponse";
import SessionModel from "../../api/models/SessionModel";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import QuranViewer from "../quran-viewer";
import SessionInfo from "./SessionInfo";
import UserSessionRoom from "./UserSessionRoom";
import ExistConfirmationModal from "../modal/ExistSessionModal";

type Props = {
  getUserSession: (sessionId: string | number) => Promise<ApiResponse>;
};

// This is wrapper for the reading room for both students and candidates
export default function LearnerCandidateReadingRoom(props: Props) {
  const { getUserSession } = props;

  const navigate = useNavigate();
  const [showTrailingContent, setShowTrailingContent] = useState(false);
  const [trailingContentIndex, setTrailingContentIndex] = useState(0);
  const [session, setSession] = useState<null | SessionModel>(null);
  const [pageNumber, setPageNumber] = useState<undefined | number>(1);

  const authContext = useAuth();
  const { id } = useParams();
  const jitsiApiRef = useRef<IJitsiMeetExternalApi>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const getSession = async () => {
    const userSessionApi = await getUserSession(id!);
    if (userSessionApi.isSuccess) {
      const userSession: SessionModel = SessionModel.fromApiResponse(
        userSessionApi.data
      );
      setSession(userSession);
      setPageNumber(userSession!.lastQuranPageReached! ?? 1);
    }
  };

  useEffect(() => {
    getSession();
  }, []);
  const viewsSize = [
    { sm: 40, md: 55, max: "360px" },
    { sm: 40, md: 50, max: "640px" },
    { sm: 40, md: 50, max: "640px" },
    { sm: 40, md: 55, max: "640px" },
  ];

  const views = [
    <div className="sm:h-screen h-full">
      {!!session ? (
        <SessionInfo
          session={session}
          onClose={() => {
            setShowTrailingContent(false);
          }}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
    <div className="sm:h-screen h-full ">
      {!!session ? (
        <QuranViewer
          onClose={(val: number | undefined) => {
            setShowTrailingContent(false);
            setTimeout(() => {
              setTrailingContentIndex(0);
            }, 10);
            setPageNumber(val);
          }}
          startPage={!!pageNumber ? pageNumber : 1}
        />
      ) : (
        <LoadingSpinner size={40} />
      )}
    </div>,
  ];
  const onCloseModal = (val: boolean) => {
    if (val) {
      navigate("/learner/acceptance-email");
      return false;
    } else {
      setIsOpen(false);
    }
  };

  const navigationItems = [
    {
      Icon: InformationCircleIcon,
      action: () => showContent(0),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: BookOpenIcon,
      action: () => showContent(1),
      color: "white",
      iconColor: "gray-600",
    },
    {
      Icon: LogoutIcon,
      action: () => {
        setIsOpen(true);
      },
      color: "red-600 rtl:rotate-180",
      iconColor: "white",
    },
  ];

  const showContent = (index: number) => {
    setShowTrailingContent(false);
    setTrailingContentIndex(index);
    setShowTrailingContent(true);
  };

  return (
    <>
      {!!session && (
        <>
          <UserSessionRoom
            showTrailingContent={showTrailingContent}
            session={session}
            navigationItems={navigationItems}
            viewsSize={viewsSize}
            jitsiApiRef={jitsiApiRef}
            views={views}
            userType={authContext?.userType}
            trailingContentIndex={trailingContentIndex}
            trailingMaxWidth={
              viewsSize[trailingContentIndex].max as "360px" | "640px"
            }
          />
          <ExistConfirmationModal isOpen={isOpen} onClose={onCloseModal} />
        </>
      )}
    </>
  );
}
